// ─── Cluster I — Renderers (job cards + change summary + report formatting) ──
// Per docs/design/app-js-split-plan-260522.md §3 Cluster I / Module 10.
//
// Second .ts module under web/static/spa/ (the first was toggles.ts, Phase 8,
// 2026-05-22). Compiled to ./renderers.js by tsc -p tsconfig.emit.json; the
// browser imports `./renderers.js` (hard-coded path). The .ts source is
// canonical; the .js sibling is a derived artifact (gitignored).
//
// Exports (public API surface — 14 symbols):
//   render                  — full re-render of queue + done lists (called by
//                              SSE, polling, upload, clearHistory)
//   renderActiveCard        — single in-progress job card
//   renderDoneCard          — single completed/failed/cancelled job card
//   renderPendingCard       — placeholder card for 429-retry pending uploads
//   renderConformanceBadges — WCAG + PDF/UA pill badges for a completed job
//   renderChangeSummary     — inline change-count summary (used by report views)
//   renderPostCheck         — automated post-remediation accessibility check row
//   renderReport            — legacy report-block (errors + advisory)
//   renderDonateSection     — donate sidebar/tab — wires DOM event handlers
//   fileIcon                — SVG <use> reference for the file's extension
//   formatFileSize          — human-readable byte size (B / KB / MB / GB)
//   friendlyIssueLabel      — short, end-user-readable label for a checker issue
//   downloadChangeReport    — async helper that builds + triggers the change-
//                              report .txt download for a completed job
//   timeSince               — elapsed-time formatter ("12s" / "3m 7s")
//
// Imports from sibling split modules:
//   globals-boot.mjs → API, esc, authFetch
//   job-state.js     → jobState (Phase 10.5 class — methods listJobs,
//                       listPendingRetries, getJob, getReportDialog,
//                       getMonotonicPct/Label/LabelPct,
//                       setMonotonicPct/Label/LabelPct),
//                       ACTIVE_STATUSES (const set), readEnvelope (pure fn)
//
// Globals (read-only at call time — typed via globals.d.ts):
//   window.adatool.log             — logging
//   window.MainJobStatus           — status-string constants
//   window.MAIN_JOB_TERMINAL_STATUSES — terminal-status Set
//   window.ErrorType               — server-error enum
//   window.getMainJobDisplayLabel  — display-collapse helper
//   window.buildChangelogText      — change-report formatter (changelog-text.js)
//   window.AdaRuleMetadata         — rule-metadata fetcher (rule-metadata-helpers.js)
//
// DOM handles (resolved at function-call time, not module-eval time):
//   #queue-list, #done-list, #queue-title, #done-title,
//   #download-all-btn, #cancel-all-btn, #clear-history-btn,
//   #donate-sidebar, #donate-tab, #donate-panel, #donate-close-btn
//
// Phase 11 (260522) closed the app.js split: spa/init.ts is the
// production entry point (loaded by index.html as <script type="module">),
// app.js is deleted, and this module's exports are the renderer surface
// the SPA actually runs. Seam tests at spa/test/renderers.seam.test.mjs
// pin the API surface.
//
// See:
//   docs/design/app-js-split-plan-260522.md (master plan)
//   docs/web/jobs-report-three-state-envelope-260517.md (readEnvelope contract)
//   docs/ux/ux-architecture-260506.md (job-lifecycle + display-collapse design)
import { API, esc, authFetch } from './globals-boot.mjs';
import { jobState, readEnvelope, ACTIVE_STATUSES } from './job-state.js';
import { fileIcon, formatFileSize, timeSince, friendlyIssueLabel, renderReport, } from './renderers-logic.js';
import { editorHrefForExt, supportsEditorExt, extOfFilename, } from '../shared/editor-routing.js';
export { fileIcon, formatFileSize, timeSince, friendlyIssueLabel, renderReport };
function _win() {
    return window;
}
function _log() {
    return window.adatool?.log;
}
// ─── Editor-surface routing (per-format review view) ────────────────────────
//
// The "Editor" button surfaces a per-format review view (PDF → WASM /editor;
// PPTX/DOCX/XLSX → the editable O365 accessibility editor at /o365-view). The
// routing contract itself lives in the shared module (web/static/shared/editor-
// routing.ts) so the /examples gallery cards reuse the SAME map (rule #11 / A.6);
// these Job-typed functions are the SPA's real surface — they extract the ext +
// handle from a `Job` and delegate to the primitive core.
/** True when the job's format has a review view (PDF editor or O365 accessibility editor). */
export function supportsEditor(job) {
    const ext = extOfFilename(job.filename);
    return ext !== null && supportsEditorExt(ext);
}
/**
 * The review-view URL for a job, or null when the format has no view. PDF opens the
 * WASM editor; PPTX/DOCX/XLSX open the O365 accessibility editor. Callers still gate on
 * `job.has_output` (a review view needs the remediated artifact).
 */
export function editorHrefForJob(job) {
    const ext = extOfFilename(job.filename);
    return ext === null ? null : editorHrefForExt(ext, job.id);
}
// ─── renderPendingCard ─────────────────────────────────────────────────────
export function renderPendingCard(_pendingId, pending) {
    return `
        <div class="job-card pending-retry" role="article" style="opacity:0.85">
            <div class="job-filename" title="${esc(pending.name)}">${fileIcon(pending.name)}<span class="filename-text">${esc(pending.name)}</span></div>
            <div class="job-meta" style="color:var(--muted)">
                <span class="pending-spinner"></span>
                Server is busy, waiting our turn…
            </div>
        </div>`;
}
// ─── render — full re-render of queue + done lists ─────────────────────────
//
// IMPORTANT: This sorts on raw backend `status` values, NOT on the
// collapsed `getMainJobDisplayLabel()` result. Intentional — sort reflects
// true processing state, not the user-facing label. If you change this to
// display-label sorting, also update admin-dashboard.ts job-list sorting
// to stay in sync.
//
// Sort: processing (remediating/checking/awaiting-check) first, then
// queued/waiting. Within processing: highest percent_done first (closest
// to done at top). Within queued: lowest queue_position first (next to
// be processed at top).
// WHY: Record<string, number> annotation is intentional — keyed by stage/status
// strings from job data at runtime. T|undefined is handled by ?? 1 fallbacks.
const _STAGE_RANK = Object.freeze({
    'remediating': 0, // actively processing
    'checking': 0,
    'awaiting-check': 0,
    'uploading': 1, // in transit
    'queued': 2, // waiting for a worker
});
export function render() {
    const W = _win();
    const MAIN_JOB_TERMINAL_STATUSES = W.MAIN_JOB_TERMINAL_STATUSES ?? new Set();
    const MainJobStatus = W.MainJobStatus ?? {};
    const jobs = jobState.listJobs().sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
    const active = jobs.filter(j => ACTIVE_STATUSES.has(j.status)).sort((a, b) => {
        const ra = _STAGE_RANK[(a.stage ?? a.status)] ?? 1;
        const rb = _STAGE_RANK[(b.stage ?? b.status)] ?? 1;
        if (ra !== rb)
            return ra - rb;
        // Within processing group: highest percent_done first.
        // Tiebreak by job_id so equal-progress jobs don't swap on every render.
        if (ra === 0) {
            const pa = typeof a.percent_done === 'number' ? a.percent_done : -1;
            const pb = typeof b.percent_done === 'number' ? b.percent_done : -1;
            if (pb !== pa)
                return pb - pa;
            return (a.id || '').localeCompare(b.id || '');
        }
        // Within queued group: lowest queue_position first (next in line at top).
        if (ra === 2) {
            const qa = typeof a.queue_position === 'number' ? a.queue_position : Infinity;
            const qb = typeof b.queue_position === 'number' ? b.queue_position : Infinity;
            return qa - qb;
        }
        // Uploading: preserve submission-time order (already sorted above).
        return 0;
    });
    const done = jobs.filter(j => MAIN_JOB_TERMINAL_STATUSES.has(j.status));
    // Pending retries (files waiting for server capacity).
    const pendingCards = jobState.listPendingRetries().map(([id, p]) => renderPendingCard(id, p));
    // totalActive intentionally includes pending retries: from the user's
    // perspective these files are still "in progress" — they received a
    // transient 429 and are awaiting an automatic retry. Each pending retry
    // card is also rendered in the queue list (as a "Server is busy,
    // waiting our turn…" card), so the count matches the number of visible
    // cards. E.g. 3 uploaded + 3 pending = "In Progress (6)".
    const totalActive = active.length + pendingCards.length;
    // Update column headings with counts.
    const queueTitle = document.getElementById('queue-title'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const doneTitle = document.getElementById('done-title'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (queueTitle)
        queueTitle.textContent = totalActive ? `In Progress (${totalActive})` : 'In Progress';
    if (doneTitle)
        doneTitle.textContent = done.length ? `Completed (${done.length})` : 'Completed';
    const queueList = document.getElementById('queue-list'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const doneList = document.getElementById('done-list'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const activeCards = active.map(renderActiveCard).join('');
    if (queueList) {
        queueList.innerHTML = totalActive
            ? activeCards + pendingCards.join('')
            : '<p class="empty-state">No files in queue</p>';
    }
    if (doneList) {
        if (done.length > 0) {
            // Preserve <details open> state on error cards across re-renders.
            // Without this, SSE-driven render() calls rebuild innerHTML and
            // collapse any expanded error details the user is reading.
            const openDetails = new Set();
            doneList.querySelectorAll('details.job-error-details[open]').forEach(d => {
                const card = (d).closest('.job-card');
                const dismissBtn = card?.querySelector('[data-dismiss-job]');
                if (dismissBtn) {
                    const id = dismissBtn.dataset.dismissJob;
                    if (id)
                        openDetails.add(id);
                }
            });
            const cards = done.reverse().map(renderDoneCard);
            if (cards.length >= 2) {
                cards[0] = '<p class="order-label">newest</p>' + cards[0];
                cards[cards.length - 1] = cards[cards.length - 1] + '<p class="order-label">oldest</p>';
            }
            doneList.innerHTML = cards.join('');
            // Re-open any <details> that were open before the re-render.
            if (openDetails.size > 0) {
                doneList.querySelectorAll('details.job-error-details').forEach(d => {
                    const card = (d).closest('.job-card');
                    const dismissBtn = card?.querySelector('[data-dismiss-job]');
                    if (dismissBtn) {
                        const id = dismissBtn.dataset.dismissJob;
                        if (id && openDetails.has(id)) {
                            (d).setAttribute('open', '');
                        }
                    }
                });
            }
        }
        else {
            doneList.innerHTML = '<p class="empty-state">No completed files</p>';
        }
    }
    // Show/hide "Download All" and "Clear" buttons.
    const completedWithOutput = jobs.filter(j => (j.status === MainJobStatus.COMPLETED || j.status === MainJobStatus.COMPLETED_UNMODIFIED)
        && j.has_output);
    const downloadAllBtn = document.getElementById('download-all-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (downloadAllBtn) {
        downloadAllBtn.style.display = completedWithOutput.length >= 2 ? 'inline-flex' : 'none';
    }
    const clearBtn = document.getElementById('clear-history-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (clearBtn) {
        clearBtn.style.display = done.length > 0 ? 'inline-flex' : 'none';
    }
    // Show/hide "Cancel All" button when there are active jobs.
    const cancelAllBtn = document.getElementById('cancel-all-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (cancelAllBtn) {
        cancelAllBtn.style.display = totalActive > 0 ? 'inline-flex' : 'none';
    }
}
// fileIcon and formatFileSize extracted to renderers-logic.ts (seam module).
// ─── renderActiveCard ──────────────────────────────────────────────────────
export function renderActiveCard(job) {
    const W = _win();
    const stage = job.stage ?? job.status;
    // Customer-facing display label — collapse the (status, stage_detail)
    // pair into one of the 4 documented phases (Initial analysis /
    // Remediating / Packaging / Validating) plus terminal labels and the
    // UI-only "Cancelling..." state. See web/static/main-job-status.js
    // and docs/ux/ux-architecture-260506.md §"Customer-facing display
    // collapse" — the renderer never inspects raw status strings here.
    // pct=5 is the static floor; the actual fill comes from job.percent_done
    // below (single source of truth from the server). Queued cards have a
    // special "Waiting in queue" surface in the job-meta line; the rest of
    // the display label comes from getMainJobDisplayLabel.
    const getMainJobDisplayLabel = W.getMainJobDisplayLabel ?? ((_j) => '');
    const stageInfo = {
        label: getMainJobDisplayLabel(job),
        pct: 5,
    };
    {
        const prevLabel = jobState.getMonotonicLabel(job.id);
        const prevLabelPct = jobState.getMonotonicLabelPct(job.id) || 0;
        const curPct = (typeof job.percent_done === 'number') ? job.percent_done : 0;
        if (prevLabel && curPct < prevLabelPct) {
            // Stale frame — keep the later-stage label we already showed.
            stageInfo.label = prevLabel;
        }
        else {
            jobState.setMonotonicLabel(job.id, stageInfo.label);
            jobState.setMonotonicLabelPct(job.id, curPct);
        }
    }
    // Meta line (bottom-left): slide/page count + stage label.
    // Size is shown in the filename area (top-right), not here.
    let meta = '';
    if (job.slide_count) {
        const fname = (job.filename ?? '').toLowerCase();
        const unit = fname.endsWith('.pptx') ? 'slides' : 'pages';
        meta += `${job.slide_count} ${unit} &middot; `;
    }
    if (stage === 'queued' && typeof job.queue_position === 'number' && job.queue_position >= 0) {
        const pos = job.queue_position + 1;
        meta += `#${pos} in queue`;
    }
    else {
        meta += stageInfo.label;
    }
    // Progress v2: the worker writes percent_done (0..100) on both
    // single and chunked remediate paths, so the UI trusts it as the
    // single source of truth. We do NOT fall back to stageInfo.pct —
    // that used to cause the bar to visibly regress from 50% (stage
    // fallback) to 10% (first real percent_done write), which made
    // users perceive the bar as "stuck / not updating". If the server
    // hasn't written percent_done yet, show a small floor (5%) so the
    // bar is visible but honest about knowing nothing.
    //
    // We also append a textual "N%" to the meta line so users see a
    // concrete number change, not just a 4px bar sliding. (The admin
    // dashboard has its own layout — this card is the user-facing one.)
    // Enforce monotonic progress — never show a lower percent than
    // previously seen for this job. Prevents confusing drops when chunk
    // workers restart or the parent aggregate lags behind individual
    // chunk SSE updates.
    const rawPct = (typeof job.percent_done === 'number') ? job.percent_done : null;
    if (rawPct !== null) {
        const prev = jobState.getMonotonicPct(job.id) ?? 0;
        job.percent_done = Math.max(prev, rawPct);
        jobState.setMonotonicPct(job.id, job.percent_done);
    }
    const pctValue = (typeof job.percent_done === 'number')
        ? Math.max(5, Math.min(100, job.percent_done))
        : 5;
    if (stage !== 'queued' && typeof job.percent_done === 'number') {
        meta += ` &middot; ${job.percent_done}%`;
    }
    const progress = stage !== 'queued'
        ? `<div class="progress-bar"><div class="fill" style="width:${pctValue}%"></div></div>` : '';
    const cancelBtn = `<button class="cancel-job-btn" data-cancel-job="${esc(job.id)}" title="Cancel" aria-label="Cancel ${esc(job.filename ?? '')}">&times;</button>`;
    const sizeLabel = job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : '';
    return `
        <div class="job-card" role="article">
            ${cancelBtn}
            <div class="job-filename" title="${esc(job.filename ?? '')}">${fileIcon(job.filename)}<span class="filename-text">${esc(job.filename ?? '')}</span>${sizeLabel}</div>
            <div class="job-meta">${meta}</div>
            ${progress}
        </div>`;
}
// ─── Dispatch table for FAILED jobs ────────────────────────────────────────
// Maps ErrorType value → render function. Each renderer receives
// (job, sharedBtns) and returns the card HTML.
// sharedBtns = { dismissBtn, retryBtn, origBtn, jobIdLine }.
// Adding a new server-side error type: add one entry here; no if-chain edits.
//
// Built lazily inside renderDoneCard because the keys reference
// window.ErrorType which is set by an external script (main-job-status.js).
// Constructing the frozen table at module-eval time would observe a partial
// or undefined ErrorType in some boot orderings.
function _buildFailedCardRenderers() {
    const ErrorType = _win().ErrorType ?? {};
    const out = {};
    if (ErrorType.TIMEOUT) {
        out[ErrorType.TIMEOUT] = (job, { dismissBtn, retryBtn, jobIdLine }) => 
        // Amber/warning card: transient failure, retry is likely to succeed.
        `<div class="job-card" role="article" style="background:#fef3c7;border-color:#f59e0b">
                ${dismissBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}" style="color:#92400e">${fileIcon(job.filename)}<span class="filename-text">&#x23F1; ${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary" style="background:transparent;color:#92400e;border:none">Processing took longer than expected. You can retry — this often succeeds on a second attempt.</div>
                ${jobIdLine}
                <div>${retryBtn}</div>
            </div>`;
    }
    if (ErrorType.VALIDATION_FAILED) {
        out[ErrorType.VALIDATION_FAILED] = (job, { dismissBtn, origBtn, jobIdLine }) => 
        // Red card: file problem, no retry — show download-original instead.
        `<div class="job-card" role="article">
                ${dismissBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}">${fileIcon(job.filename)}<span class="filename-text">&#x26A0; ${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary">We detected a problem with the remediated file. Your original file is preserved. Our team has been notified.</div>
                ${jobIdLine}
                <div>${origBtn}</div>
            </div>`;
    }
    if (ErrorType.QUOTA_EXCEEDED) {
        out[ErrorType.QUOTA_EXCEEDED] = (job, { dismissBtn, jobIdLine }) => {
            // Orange card: the credit/quota gate declined this job BEFORE
            // remediation (free-tier-pagecap-silent-truncation-ux-261005, C-L1).
            //
            // The body is the SERVER's authored, number-bearing reason
            // (job.failure.user_message) — the SAME authored-copy contract the
            // default failed card honors (§595). This renderer is a PURE CONSUMER,
            // never an authoring site: the 260821 Beta copy sweep updated the
            // server sites but missed THIS card (the second-surface class, A.24),
            // leaving legacy "Upgrade your plan or wait for your quota to reset"
            // copy that is false on all three claims for the Beta (no plans/tiers,
            // the anon grant is one-time-lifetime so nothing "resets", /account
            // offers no upgrade). Falling back to the catalogue banner (itself
            // Beta-honest post Fold 1 — no "upgrade"/"plan") when the block is
            // absent keeps authored copy on every path (INV-PC-3); NEVER hardcode
            // plan/period/upgrade copy here again.
            const body = job.failure?.user_message
                ?? 'This file is larger than your free quota allows. Reduce the number of pages or the file size and try again, or sign in for more.';
            // Page-framing line (C-L1) uses ONLY server-provided job.slide_count —
            // never a client-mirrored allowance constant (the allowance already
            // lives in the server body copy; mirroring it is the A.24 second-source
            // smell, Fold 4). Omitted when slide_count is absent (the DOCX-estimate
            // fidelity path leaves it NULL) so the card never reads "undefined
            // pages" (INV-PC-3 / D-3 slide_count-NULL case).
            const pageLine = (typeof job.slide_count === 'number' && job.slide_count > 0)
                ? `<div class="job-error-pagecount" style="color:#9a3412;font-size:0.9em;margin-top:2px">This document has ${job.slide_count} ${(job.filename ?? '').toLowerCase().endsWith('.pptx') ? 'slides' : 'pages'}.</div>`
                : '';
            // No Upgrade link (dead /account route) and no Retry button — an
            // unmodified retry cannot pass the same gate (INV-PC-4). The body copy
            // carries the honest next step (sign in with a Purdue email / for a
            // fresh free allowance).
            return `<div class="job-card" role="article" style="background:#fff7ed;border-color:#f97316">
                ${dismissBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}" style="color:#9a3412">${fileIcon(job.filename)}<span class="filename-text">&#x1F6AB; ${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary" style="background:transparent;color:#9a3412;border:none">${esc(body)}</div>
                ${pageLine}
                ${jobIdLine}
            </div>`;
        };
    }
    if (ErrorType.UPLOAD_FAILED) {
        out[ErrorType.UPLOAD_FAILED] = (job, { dismissBtn, jobIdLine }) => 
        // Yellow card: upload failed — re-upload prompt, not retry.
        `<div class="job-card" role="article" style="background:#fef9c3;border-color:#eab308">
                ${dismissBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}" style="color:#713f12">${fileIcon(job.filename)}<span class="filename-text">&#x2B06; ${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary" style="background:transparent;color:#713f12;border:none">The file couldn't be uploaded. Please check the file and try again.</div>
                ${jobIdLine}
            </div>`;
    }
    return out;
}
// ─── renderDoneCard ────────────────────────────────────────────────────────
export function renderDoneCard(job) {
    const W = _win();
    const MainJobStatus = W.MainJobStatus ?? {};
    const ErrorType = W.ErrorType ?? {};
    const dismissBtn = `<button class="dismiss-btn" data-dismiss-job="${esc(job.id)}" title="Dismiss" aria-label="Dismiss ${esc(job.filename ?? '')}">&times;</button>`;
    const remediateAgainBtn = job.status === MainJobStatus.COMPLETED
        ? `<button class="remediate-again-btn" data-remediate-again="${esc(job.id)}" title="Re-remediate this file" aria-label="Re-remediate ${esc(job.filename ?? '')}">&#x21BB;</button>`
        : '';
    // Distinct card for the corrupt-output fallback path. The user receives
    // the original unmodified file (not a remediated one); we surface this
    // visually rather than silently showing a green checkmark.
    if (job.status === MainJobStatus.COMPLETED_UNMODIFIED) {
        const retryBtn = `<button class="btn btn-small" data-retry-job="${esc(job.id)}" style="margin-top:6px">Retry</button>`;
        const dlBtn = job.has_output
            ? `<a href="${API}/api/jobs/${esc(job.id)}/download" class="btn btn-primary btn-small" style="margin-top:6px" title="Download original (unmodified) file">Download</a>`
            : '';
        const jobIdLine = `<div class="job-error-jobid">Job ID: ${esc(job.id)}</div>`;
        return `
            <div class="job-card" role="article" style="background:#fff7ed;border-color:#f97316">
                ${dismissBtn}
                ${remediateAgainBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}" style="color:#9a3412">&#x26A0; <span class="filename-text">${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary" style="background:transparent;color:#9a3412;border:none">We could not remediate this file — your original is returned unmodified. Reasons may include an unsupported format, encrypted content, or a processing error. Retry may succeed; use the feedback button to report if it persists.</div>
                ${jobIdLine}
                <div style="display:flex;gap:6px;flex-wrap:wrap">${retryBtn}${dlBtn}</div>
            </div>`;
    }
    if (job.status === MainJobStatus.FAILED) {
        // Normalise to a known ErrorType value; unknown server values fall
        // through to the internal_error default renderer below.
        const errorType = job.error_type ?? ErrorType.INTERNAL_ERROR;
        const retryBtn = `<button class="btn btn-small" data-retry-job="${esc(job.id)}" style="margin-top:6px">Retry</button>`;
        const origBtn = `<a href="${API}/api/jobs/${esc(job.id)}/download-original" class="btn btn-small" style="background:#777;color:#fff;margin-top:6px" title="Download original (unmodified) file">Download Original</a>`;
        // Small grey subtitle shown under every failure message. Low-profile
        // UX-wise but lets users paste the job id when reporting an issue —
        // saves us a round-trip asking "which file, what time".
        const jobIdLine = `<div class="job-error-jobid">Job ID: ${esc(job.id)}</div>`;
        const renderers = _buildFailedCardRenderers();
        const renderer = errorType ? renderers[errorType] : undefined;
        if (renderer) {
            return renderer(job, { dismissBtn, retryBtn, origBtn, jobIdLine });
        }
        // Default: internal_error or unknown error type.
        //
        // LEAK FIX (user-visible-message-catalogue-260711, §5 L4). The USER body
        // is the SERVER-rendered catalogue string (job.failure.user_message) —
        // NEVER the raw stored job.error, which can carry an internal exception
        // string ("Parent job … was marked failed during chunk polling"). The
        // server always attaches a cause-correct failure block to a FAILED job
        // (routes_jobs.py:_attach_failure_block → message_for(cause).user); this
        // render site is a pure consumer of that authored copy. A generic
        // catalogue-shaped fallback covers the (should-not-happen) case where the
        // block is absent — still authored copy, never job.error.
        const userMessage = job.failure?.user_message
            ?? 'Something went wrong while processing this file. Please retry, and if the problem persists, use the feedback button to report it.';
        // The diagnostic shown in the collapsed <details> is the SERVER-SANITIZED
        // job.diagnostic (ops_title + typed cause + job id) — NEVER the raw stored
        // error, which the server now curates out of the owning-user response
        // (staging-strictchunkless-leak-rca-260915). No internal exception string
        // can reach this <pre> because the server no longer hands the SPA one.
        // overflow-wrap:anywhere on .job-error-full keeps the expanded <pre>
        // inside the card box even for long strings.
        const diag = typeof job.diagnostic === 'string' ? job.diagnostic : '';
        // Only surface the expander when the diagnostic adds information. When it is
        // empty OR merely repeats the user-facing headline verbatim, the disclosure
        // is pure redundancy: it invites a click that reveals nothing new. Gate on a
        // trimmed inequality so a redundant expander never renders.
        const diagIsInformative = diag.trim() !== '' && diag.trim() !== userMessage.trim();
        const diagHtml = diagIsInformative
            ? `<details class="job-error-details">
                    <summary class="job-error-diag-summary">Diagnostic detail</summary>
                    <pre class="job-error-full">${esc(diag)}</pre>
                </details>`
            : '';
        return `
            <div class="job-card" role="article">
                ${dismissBtn}
                <div class="job-filename" title="${esc(job.filename ?? '')}">${fileIcon(job.filename)}<span class="filename-text">${esc(job.filename ?? '')}</span>${job.size ? `<span class="job-filesize">${formatFileSize(job.size)}</span>` : ''}</div>
                <div class="job-error-summary">${esc(userMessage)}</div>
                ${diagHtml}
                ${jobIdLine}
                <div style="display:flex;gap:6px;flex-wrap:wrap">${retryBtn}${origBtn}</div>
            </div>`;
    }
    let meta = '';
    if (job.started_at && job.finished_at) {
        meta += timeSince(job.started_at, job.finished_at);
    }
    const postCheck = renderPostCheck(job);
    // Build a single-row layout: time on left, fixes count + Changelog +
    // downloads + editor on right.
    const metaSpan = meta ? `<span class="job-meta" style="margin:0;font-size:11px;white-space:nowrap">${meta}</span>` : '';
    // "Original" — proper button-card sibling of "Download" so the
    // affordance reads as clickable. Visually de-emphasised (outline, not
    // filled) so it defers to the primary Download CTA.
    const oldLink = `<a href="${API}/api/jobs/${job.id}/download-original" class="btn btn-small job-original-btn" title="Download unmodified original file">Original</a>`;
    const newBtn = job.has_output
        ? `<a href="${API}/api/jobs/${job.id}/download" class="btn btn-primary btn-small" title="Download remediated file">Download</a>`
        : '';
    // "Editor" — prominent pencil-icon + label button. Opens a per-format
    // review surface in a new tab: PDF → the WASM /editor; PPTX/DOCX → the
    // read-only O365 diff-view /o365-view (Epic pptx-diff-view-260714 /
    // docx-diff-view-260714 — the served-page reachability P1). The SVG is
    // aria-hidden; the visible "Editor" text supplies the accessible name.
    const editIcon = `<svg class="job-edit-icon" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 17 L3 14 L13.5 3.5 a1.4 1.4 0 0 1 2 0 l1 1 a1.4 1.4 0 0 1 0 2 L6 17 Z"/><line x1="12" y1="5" x2="15" y2="8"/><line x1="3" y1="17" x2="6" y2="17"/></svg>`;
    const editHref = editorHrefForJob(job);
    const editBtn = (editHref && job.has_output)
        ? `<a href="${editHref}" class="btn btn-small job-edit-btn" target="_blank" rel="noopener" title="Open in editor">${editIcon}<span style="margin-left:4px">Editor</span></a>`
        : '';
    let middleContent = '';
    if (job.report) {
        // Envelope-aware: read total_changes from derived_changelog when
        // present (single-file path), else from legacy top-level fields
        // (pre-260517 chunked).
        const env = readEnvelope(job);
        // For legacy chunked, prefer item-count over total_changes when
        // present (existing behaviour: total_items aggregates across
        // categories' changes[] arrays).
        let n;
        if (env.kind === 'envelope') {
            n = env.totalChanges;
        }
        else {
            n = env.totalChanges
                || env.categories.reduce((sum, g) => sum + ((g.changes) ?? []).length, 0)
                || 0;
        }
        if (n === 0) {
            // No changes. 5-state read contract (architect §7 ruling #3):
            // `no_issues_found` → the descriptive affirmation;
            // `legacy_no_data` (pre-260517 / un-backfilled — never a stored
            // status) → the legacy "(no changes applied)" copy, an honest
            // graceful degradation, NOT a false affirmation.
            const affirmation = env.status === 'no_issues_found' && env.summary
                ? `<span class="no-changes" title="${esc(env.summary)}">${esc(env.summary)}</span>`
                : `<span class="no-changes">(no changes applied)</span>`;
            middleContent = `${affirmation}
                <span style="margin-left:auto;display:flex;align-items:center;gap:6px">${oldLink}${newBtn}${editBtn}</span>`;
        }
        else {
            const label = n === 1 ? '1 change applied' : `${n} changes applied`;
            const reportLink = `<a href="#" class="report-link" data-download-report="${esc(job.id)}" style="font-size:11px" title="Download the formatted accessibility change report">List of changes</a>`;
            middleContent = `<span style="font-size:13px">${esc(label)}</span>
                <span style="margin-left:auto;display:flex;align-items:center;gap:6px">${reportLink}${oldLink}${newBtn}${editBtn}</span>`;
        }
    }
    else {
        // No report (e.g. failed before generating one) — just show downloads.
        middleContent = `<span style="margin-left:auto;display:flex;align-items:center;gap:6px">${oldLink}${newBtn}${editBtn}</span>`;
    }
    let doneSizeLabel = '';
    if (job.size) {
        if (job.output_size) {
            doneSizeLabel = `<span class="job-filesize">${formatFileSize(job.size)} → ${formatFileSize(job.output_size)}</span>`;
        }
        else {
            doneSizeLabel = `<span class="job-filesize">${formatFileSize(job.size)}</span>`;
        }
    }
    // Re-renders of the done-list (driven by SSE / polling) must not
    // collapse a "Report a problem" dialog the user has already opened,
    // nor erase a half-typed message. jobState.getReportDialog(jobId) is
    // the source of truth.
    const reportOpen = jobState.getReportDialog(job.id);
    const reportLinkArrow = reportOpen ? '▾' : '▸';
    const reportFormDisplay = reportOpen ? 'block' : 'none';
    const reportDraft = (reportOpen?.text) ? esc(reportOpen.text) : '';
    const inlineFeedback = `
        <div class="job-feedback-link" data-feedback-job="${esc(job.id)}">${reportLinkArrow} Report a problem</div>
        <div class="job-feedback-form" style="display:${reportFormDisplay}">
            <textarea class="job-feedback-textarea" placeholder="What went wrong?" rows="2">${reportDraft}</textarea>
            <button class="feedback-submit-btn" data-feedback-submit="${esc(job.id)}">Send</button>
        </div>`;
    const conformanceBadges = renderConformanceBadges(job);
    // Silent-degradation soft-warning. When the C# CLI's stats block
    // reported imagesFailed > 0, the API surfaces a `partial_completion`
    // sibling field carrying {count, message}. Apply `card--partial` so
    // the user sees a yellow outline, and render the retry-recommended
    // message under the title. Status stays 'completed' — the file is real
    // and downloadable.
    //
    // form_field_partial_completion is a parallel sibling for AcroForm
    // widgets that didn't survive the slice/merge round-trip — sourced
    // from PdfCatalogEntryCopier.CopyAcroForm's kidsMissed canary. Both
    // signals can co-occur on a single job (chunked PDF with form fields
    // AND missing alt text on the images on the same chunk); render their
    // messages in sequence and apply the yellow outline if either is set.
    const partial = job.partial_completion;
    const formFieldPartial = job.form_field_partial_completion;
    // inherited_vendor_brackets is a parallel sibling for InDesign-style
    // /PlacedPDF /MC0 BDC ... EMC pairs detected on the input. Always
    // surfaces a yellow card; the wording differs between default mode
    // (preserved — opt-in repair available) and aggressive mode (repaired
    // — verify rendered output). See docs/pdf/pdf-figure-placedpdf-nesting.md.
    const vendorBrackets = job.inherited_vendor_brackets;
    const anyPartial = (partial ?? formFieldPartial) ?? vendorBrackets;
    const cardClasses = anyPartial ? 'job-card card--partial' : 'job-card'; // role="article" added in HTML template below
    let partialMessage = '';
    if (partial) {
        partialMessage += `<p class="partial-completion-message">${esc(partial.message ?? '')}</p>`;
    }
    if (formFieldPartial) {
        partialMessage += `<p class="partial-completion-message">${esc(formFieldPartial.message ?? '')}</p>`;
    }
    if (vendorBrackets) {
        partialMessage += `<p class="partial-completion-message">${esc(vendorBrackets.message ?? '')}</p>`;
    }
    return `
        <div class="${cardClasses}" role="article">
            ${dismissBtn}
            ${remediateAgainBtn}
            <div class="job-filename" title="${esc(job.filename ?? '')}">${fileIcon(job.filename)}<span class="filename-text">${esc(job.filename ?? '')}</span>${doneSizeLabel}${conformanceBadges}</div>
            ${partialMessage}
            <div style="display:flex;align-items:center;gap:8px">
                ${metaSpan}
                ${middleContent}
            </div>
            ${postCheck}
            ${inlineFeedback}
        </div>`;
}
// ─── renderConformanceBadges ───────────────────────────────────────────────
// Render the WCAG + PDF/UA conformance badges for a completed job. Returns
// the HTML for zero, one, or two pill badges (WCAG always renders when
// rated; PDF/UA only renders for conformant PDFs). The CSS classes
// correspond to the .conformance-badge family in style.css.
//
// Tooltip text mirrors the calibration disclaimer the user requested —
// these are an automated-judgment estimate, not a formal audit.
export function renderConformanceBadges(job) {
    const W = _win();
    const MainJobStatus = W.MainJobStatus ?? {};
    if (!job || job.status !== MainJobStatus.COMPLETED)
        return '';
    const wcag = job.wcag_estimate;
    const pdfua = job.pdfua_conformance;
    if (!wcag && !pdfua)
        return '';
    const tooltipBase = 'WCAG 2.2 estimate based on automated review against ' +
        'Matterhorn Protocol + WCAG 2.2 success criteria. ' +
        'Calibration in progress — this is an automated-judgment ' +
        'estimate, not a formal audit.';
    const parts = [];
    if (wcag === 'AAA') {
        parts.push('<span class="conformance-badge conformance-aaa" title="' +
            esc(tooltipBase) + '">' +
            '<span class="conformance-icon" aria-hidden="true">' +
            '\u{1F3C6}' + '</span>WCAG AAA</span>');
    }
    else if (wcag === 'AA') {
        parts.push('<span class="conformance-badge conformance-aa" title="' +
            esc(tooltipBase) + '">WCAG AA</span>');
    }
    else if (wcag === 'A') {
        parts.push('<span class="conformance-badge conformance-a" title="' +
            esc(tooltipBase) + '">WCAG A</span>');
    }
    else if (wcag === 'below_a') {
        parts.push('<span class="conformance-badge conformance-below-a" title="' +
            esc(tooltipBase) + '">A11y-attempted, Level A unmet</span>');
    }
    // 'unrated' or null: render nothing for WCAG.
    if (pdfua === 'conformant') {
        const pdfuaTooltip = 'PDF/UA-1 conformance estimated based on ' +
            'Matterhorn Protocol 1.1 human-judgment failure conditions. ' +
            'Calibration in progress — automated estimate, not a formal audit.';
        parts.push('<span class="conformance-badge pdfua" title="' +
            esc(pdfuaTooltip) + '">' +
            '<span class="conformance-icon" aria-hidden="true">' +
            '✓' + '</span>PDF/UA</span>');
    }
    return parts.join('');
}
// ─── renderChangeSummary ───────────────────────────────────────────────────
export function renderChangeSummary(job) {
    // Envelope-aware count; falls back to legacy top-level fields.
    const env = readEnvelope(job);
    const n = env.totalChanges || 0;
    const label = n === 1 ? '1 change applied' : `${n} changes applied`;
    return `<div class="report-inline report-clean" style="display:flex;align-items:center;justify-content:space-between">
        <span>${esc(label)}</span>
        <a href="#" class="btn btn-small" data-download-report="${esc(job.id)}" title="Download the formatted accessibility change report">Download change report</a>
    </div>`;
}
// ─── renderPostCheck ───────────────────────────────────────────────────────
export function renderPostCheck(job) {
    const W = _win();
    const MainJobStatus = W.MainJobStatus ?? {};
    // Only show for remediated jobs (not check-only).
    if (job.status !== MainJobStatus.COMPLETED || !job.has_output)
        return '';
    const pc = job.post_check;
    // If the checker ran but there's an error field, it's a partial/failed
    // run — still show what we have, but don't promise it's complete.
    if (!pc)
        return '';
    const issues = pc.issues ?? [];
    const errors = issues.filter(i => i.severity === 'error');
    const warnings = issues.filter(i => i.severity === 'warning');
    // info-only items are too noisy for the card; they're in the download report.
    let icon;
    let text;
    let cls;
    if (pc.error) {
        // Checker itself errored — don't show anything misleading.
        return '';
    }
    else if (errors.length === 0 && warnings.length === 0) {
        icon = '&#10003;';
        text = 'Automated accessibility check passed';
        cls = 'post-check-pass';
        return `<div class="post-check-row ${cls}">${icon} ${text}</div>`;
    }
    const parts = [];
    if (errors.length)
        parts.push(`${errors.length} error${errors.length > 1 ? 's' : ''}`);
    if (warnings.length)
        parts.push(`${warnings.length} warning${warnings.length > 1 ? 's' : ''}`);
    icon = '&#9432;';
    text = `Automated check: ${parts.join(', ')} remaining — review in your document app`;
    cls = errors.length ? 'post-check-issues' : 'post-check-warnings';
    // Two surfaces for the issue list:
    //   1. Inline <ul> beneath the summary — top few visible.
    //   2. Native title= tooltip on the row — the full list, for users
    //      who want to read everything without downloading the change
    //      report.
    // The downloadable change report (downloadChangeReport) carries the
    // full list as well; the three surfaces should agree.
    const ordered = errors.concat(warnings);
    const VISIBLE_LIMIT = 5;
    const shown = ordered.slice(0, VISIBLE_LIMIT);
    const overflow = ordered.length - shown.length;
    // Inline list — short labels, severity icon, page locator, rule_id pill.
    const items = shown.map(it => {
        const label = friendlyIssueLabel(it);
        const sevClass = it.severity === 'error' ? 'pc-sev-error' : 'pc-sev-warn';
        const sevIcon = it.severity === 'error' ? '&#9888;' : '&#9432;'; // ⚠ / ⓘ
        /* eslint-disable @typescript-eslint/no-base-to-string -- it.location/rule_id are wire IO typed as unknown; String() is the explicit conversion already applied */
        const locStr = it.location ? ` (${esc(String(it.location))})` : '';
        /* eslint-enable @typescript-eslint/no-base-to-string */
        const loc = (it.page_number != null) ? ` (page ${it.page_number})` : locStr;
        const rid = it.rule_id ? `<span class="pc-rid" title="Rule ID ${esc(String(it.rule_id))}">${esc(String(it.rule_id))}</span>` : '';
        return `<li class="${sevClass}"><span class="pc-icon" aria-hidden="true">${sevIcon}</span><span class="pc-text">${esc(label)}${loc}</span>${rid}</li>`;
    }).join('');
    const more = overflow > 0
        ? `<li class="pc-more">…and ${overflow} more — see the change report for the full list</li>`
        : '';
    // Tooltip — plain-text full enumeration. title= can't render HTML so
    // we newline-separate. Each line: severity tag, label, page if any,
    // rule_id. Truncate at 80 chars per line so a stray verbose
    // description doesn't explode the tooltip.
    const tooltipLines = ordered.map(it => {
        const sev = it.severity === 'error' ? '⚠ ' : it.severity === 'warning' ? 'ⓘ ' : '· ';
        const label = friendlyIssueLabel(it);
        /* eslint-disable @typescript-eslint/no-base-to-string -- it.location/rule_id are wire IO typed as unknown; String() is the explicit conversion already applied */
        const locStr2 = it.location ? ` (${String(it.location)})` : '';
        /* eslint-enable @typescript-eslint/no-base-to-string */
        const loc = (it.page_number != null) ? ` (page ${it.page_number})` : locStr2;
        const rid = it.rule_id ? ` [${String(it.rule_id)}]` : '';
        const line = `${sev}${label}${loc}${rid}`;
        return line.length > 100 ? line.slice(0, 97) + '…' : line;
    });
    const tooltipText = tooltipLines.length === 0 ? ''
        : `Full list of remaining issues:\n${tooltipLines.join('\n')}`;
    const tooltipAttr = tooltipText ? ` title="${esc(tooltipText)}"` : '';
    return `<div class="post-check-row ${cls}"${tooltipAttr}>
        <div class="post-check-summary">${icon} ${text}</div>
        <ul class="post-check-issues-list">${items}${more}</ul>
    </div>`;
}
// _KNOWN_ISSUE_LABELS and friendlyIssueLabel extracted to renderers-logic.ts (seam module).
// ─── downloadChangeReport ──────────────────────────────────────────────────
export async function downloadChangeReport(jobId) {
    const W = _win();
    const log = _log();
    const job = jobState.getJob(jobId);
    if (!job?.report) {
        log?.warn('downloadChangeReport: no report for job', jobId);
        return;
    }
    // Resolve rule metadata so the formatter can render the "Standards
    // Advanced" section. The shared helper caches across calls, so a
    // repeat download doesn't re-fetch. Failure mode is graceful: an
    // empty ruleMetadata map causes the section to be skipped silently
    // per Phase-2 design doc §5.
    let ruleMetadata = null;
    try {
        if (W.AdaRuleMetadata) {
            ruleMetadata = await W.AdaRuleMetadata.fetchRuleMetadata();
        }
    }
    catch (err) {
        // Not silent — log + fall through to a report without the new
        // section.
        log?.warn('downloadChangeReport: rule-metadata fetch failed', err);
    }
    // Touch authFetch via a no-op reference so the import is preserved
    // for the upcoming `loadUserJobHistory` consumer (the cross-page
    // history-list pager). The rest of this function does not call it.
    void authFetch;
    // Envelope-aware: when the wrapper-only envelope is the report's only
    // top-level data, pass an inner-shape view whose total_changes +
    // categories come from the envelope. Legacy chunked reports (no
    // envelope) pass through unchanged.
    const env = readEnvelope(job);
    const reportForFormatter = env.kind === 'envelope'
        ? Object.assign({}, job.report, { total_changes: env.totalChanges, categories: env.categories })
        : job.report;
    if (!W.buildChangelogText) {
        log?.warn('downloadChangeReport: window.buildChangelogText missing'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        return;
    }
    const text = W.buildChangelogText(reportForFormatter, {
        variant: 'full',
        filename: job.filename,
        finishedAt: job.finished_at,
        wcagEstimate: job.wcag_estimate,
        pdfuaConformance: job.pdfua_conformance,
        postCheck: job.post_check,
        ruleMetadata: ruleMetadata,
        warnFn: log?.warn?.bind(log),
    });
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    a.href = url;
    const baseName = (job.filename ?? 'file').replace(/\.[^.]+$/, '');
    a.download = baseName + '-changes.txt';
    a.style.display = 'none';
    document.body.appendChild(a); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    a.click();
    setTimeout(() => {
        document.body.removeChild(a); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        URL.revokeObjectURL(url);
    }, 1000);
    log?.info('[ada-tool] downloaded change report for job', jobId);
}
// renderReport extracted to renderers-logic.ts (seam module).
// timeSince extracted to renderers-logic.ts (seam module).
// ─── renderDonateSection ───────────────────────────────────────────────────
// Show the donate sidebar (right side, mirrors feedback sidebar on left).
// Wires up the tab toggle + close-button + Escape-to-close handlers.
// Idempotent via the module-internal `_donateRendered` flag.
let _donateRendered = false;
export function renderDonateSection() {
    if (_donateRendered)
        return;
    _donateRendered = true;
    const sidebar = document.getElementById('donate-sidebar'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!sidebar)
        return;
    sidebar.style.display = '';
    const tab = document.getElementById('donate-tab'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const panel = document.getElementById('donate-panel'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const donateCloseBtn = document.getElementById('donate-close-btn'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!tab || !panel)
        return;
    function openPanel() {
        panel.style.display = 'block';
        tab.setAttribute('aria-expanded', 'true');
    }
    function closePanel() {
        panel.style.display = 'none';
        tab.setAttribute('aria-expanded', 'false');
    }
    tab.addEventListener('click', () => {
        if (panel.style.display !== 'none')
            closePanel();
        else
            openPanel();
    });
    if (donateCloseBtn) {
        donateCloseBtn.addEventListener('click', closePanel);
    }
    // Escape key closes the panel (matches feedback sidebar behavior).
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && panel.style.display !== 'none') {
            closePanel();
            (tab).focus();
        }
    });
}
